/**
 * Copyright (c) 2025 Répét Contributors
 * Licensed under the MIT License
 * See LICENSE file in the project root for full license text
 */

import { apiClient } from '../lib/api/client'
import { db } from '../core/storage/database'
import {
  upsertTextFromServer,
  upsertVersionFromServer,
  deleteTextLocally,
  deleteVersionLocally,
} from '../core/storage/textsRepository'
import {
  upsertGroupFromServer,
  upsertGroupMemberFromServer,
  upsertGroupTextFromServer,
  purgeStaleGroups,
  purgeStaleGroupMembers,
  purgeStaleGroupTexts,
} from '../core/storage/groupsRepository'
import { versionConfigService } from './versionConfigService'
import type { VersionConfigResponse } from '../core/models/VersionConfig'

// ============================================================================
// Types publics
// ============================================================================

export interface SyncState {
  isSyncing: boolean
  lastSyncAt: string | null
  lastError: string | null
}

interface SnapshotText {
  id: string
  ownerId: string
  title: string
  author?: string
  year?: string
  description?: string
  currentVersionId?: string
  originalVersionId?: string
  deletedAt?: string | null
  createdAt: string
  updatedAt: string
  isShared?: boolean
  sharedGroupId?: string
}

interface SnapshotVersion {
  id: string
  textId: string
  fileName: string
  ast: Record<string, unknown>
  contentHash: string
  voiceAssignments: unknown[]
  ownerVersionNumber: number
  versionLabel: string
  changeDescription?: string
  parentVersionId?: string
  createdById: string
  deviceId: string
  deletedAt?: string | null
  createdAt: string
  updatedAt: string
}

interface SharedTextEntry {
  groupTextId: string
  groupId: string
  text: SnapshotText
  version: SnapshotVersion
  config?: VersionConfigResponse | null
}

interface SnapshotGroup {
  id: string
  name: string
  description?: string
  ownerId: string
  createdAt: string
  updatedAt: string
}

interface SnapshotGroupMember {
  id: string
  groupId: string
  userId: string
  role: 'OWNER' | 'EDITOR' | 'VIEWER'
  joinedAt: string
  userEmail?: string
  userName?: string
}

interface SnapshotGroupText {
  id: string
  groupId: string
  textId: string
  versionId: string
  publishedById: string
  publishedAt: string
  publishedByName?: string
  textTitle?: string
  textAuthor?: string
  versionLabel?: string
  ownerDisplayName?: string
  hasAudio?: boolean
  audioDuration?: number
}

interface SnapshotResponse {
  serverTime: string
  texts: SnapshotText[]
  versions: SnapshotVersion[]
  configs: VersionConfigResponse[]
  sharedTexts?: SharedTextEntry[]
  groups?: SnapshotGroup[]
  groupMembers?: SnapshotGroupMember[]
  groupTexts?: SnapshotGroupText[]
}

interface CompilationForVersionResponse {
  id: string
  status: string
  textVersionId: string
  progress?: number
  linesProcessed?: number
  linesTotal?: number | null
  currentStep?: string | null
  completedAt?: string | null
}

// ============================================================================
// État interne
// ============================================================================

const LAST_SYNC_AT_KEY_PREFIX = 'lastSyncAt'
const SYNC_INTERVAL_MS = 60_000
const CLOCK_MARGIN_MS = 5_000
const FIRST_SYNC_SENTINEL = '1970-01-01T00:00:00Z'

let _isSyncing = false
let _lastError: string | null = null
let _pollingTimer: ReturnType<typeof setInterval> | null = null
let _hasRunCleanup = false
let _currentUserId: string | null = null

// Observers UI
const _observers = new Set<(state: SyncState) => void>()

// Entités actives (TextDetailScreen / ReadingScreen ouverts)
const activeEntities = new Map<string, 'text' | 'version'>()

// ============================================================================
// Helpers état
// ============================================================================

function _lastSyncAtKey(userId: string): string {
  return `${LAST_SYNC_AT_KEY_PREFIX}:${userId}`
}

function _getLastSyncAt(userId: string): string | null {
  return localStorage.getItem(_lastSyncAtKey(userId))
}

function _setLastSyncAt(userId: string, ts: string): void {
  localStorage.setItem(_lastSyncAtKey(userId), ts)
}

function _getSyncState(): SyncState {
  return {
    isSyncing: _isSyncing,
    lastSyncAt: _currentUserId ? _getLastSyncAt(_currentUserId) : null,
    lastError: _lastError,
  }
}

function _notify(): void {
  const state = _getSyncState()
  _observers.forEach((cb) => {
    try {
      cb(state)
    } catch {
      // Observer silencieux
    }
  })
}

// ============================================================================
// API publique — gestion des entités actives
// ============================================================================

export function registerActiveEntity(id: string, type: 'text' | 'version'): void {
  activeEntities.set(id, type)
}

export function unregisterActiveEntity(id: string): void {
  activeEntities.delete(id)
}

// ============================================================================
// Sync des audios manquants
// ============================================================================

async function _syncMissingAudios(): Promise<void> {
  try {
    const versions = await db.textVersions.filter((v) => !v.deletedAt).toArray()
    console.warn(`[syncService] _syncMissingAudios: checking ${versions.length} versions`)

    await Promise.allSettled(
      versions.map(async (version) => {
        try {
          console.warn(`[syncService] Checking server for version ${version.id}`)

          // Vérifier si une compilation COMPLETED existe localement
          const localCompleted = await db.compilations
            .where('textVersionId')
            .equals(version.id)
            .filter((c) => c.status === 'COMPLETED')
            .first()

          if (localCompleted) {
            // ✅ NOUVEAU : Valider que l'audio est vraiment présent
            const { validateCompilationAudio } = await import('./audioValidationService')
            const hasAudio = await validateCompilationAudio(localCompleted.id)
            if (hasAudio) {
              console.warn(
                `[syncService] Version ${version.id}: audio already downloaded, skipping`
              )
              return
            }

            // ⚠️ État incohérent détecté : COMPLETED mais pas d'audio
            console.warn(
              `[syncService] Version ${version.id}: compilation ${localCompleted.id} ` +
                `marked COMPLETED but audio missing - triggering retry download`
            )

            // Retry download (non-bloquant)
            const { downloadCompilationZip } = await import('./compilationService')
            downloadCompilationZip(localCompleted.id).catch((err) => {
              console.error(`[syncService] Retry download failed for ${localCompleted.id}:`, err)
            })
            return // Ne pas re-fetch le serveur
          }

          // Interroger le serveur
          const serverCompilation = await apiClient.get<CompilationForVersionResponse>(
            `/audio/versions/${version.id}/compilation`
          )

          if (serverCompilation?.status === 'COMPLETED') {
            console.warn(`[syncService] Server has COMPLETED compilation ${serverCompilation.id}`)
            // Persister la compilation en IndexedDB avant de télécharger
            // (requis pour les textes partagés dont la compilation appartient au propriétaire)
            const existing = await db.compilations.get(serverCompilation.id)
            if (!existing) {
              await db.compilations.put({
                id: serverCompilation.id,
                textVersionId: serverCompilation.textVersionId,
                userId: '',
                status: 'COMPLETED',
                progress: serverCompilation.progress ?? 100,
                linesProcessed: serverCompilation.linesProcessed ?? 0,
                linesTotal: serverCompilation.linesTotal ?? undefined,
                currentStep: serverCompilation.currentStep ?? undefined,
                completedAt: serverCompilation.completedAt ?? undefined,
                createdAt: new Date().toISOString(),
                serverId: serverCompilation.id,
                audioFormat: 'opus',
                audioBitrate: 32,
                cacheHits: 0,
                cacheMisses: 0,
              })
              console.warn(`[syncService] Compilation persisted to IndexedDB, triggering download`)
            }

            // Déclencher le téléchargement en arrière-plan (non-bloquant)
            const { downloadCompilationZip } = await import('./compilationService')
            downloadCompilationZip(serverCompilation.id).catch((err) => {
              console.error(
                `[syncService] ZIP download failed for compilation ${serverCompilation.id}:`,
                err
              )
              // Réessayé à la prochaine sync via validation avant skip
            })
          }
        } catch (err) {
          // 404 = pas de compilation → normal, silencieux
          // Autres erreurs → loggées pour diagnostic
          if (err && typeof err === 'object' && 'status' in err && err.status !== 404) {
            console.error(
              `[syncService] Error checking compilation for version ${version.id}:`,
              err
            )
          }
        }
      })
    )
  } catch {
    // Erreur générale — ne bloque pas la sync principale
  }
}

// ============================================================================
// triggerSync
// ============================================================================

export async function triggerSync(userId: string): Promise<void> {
  // Verrou — une seule sync à la fois
  if (_isSyncing) {
    console.warn('[SyncService] triggerSync — verrou actif, sync ignorée')
    return
  }

  // Offline → pas de sync
  if (!navigator.onLine) {
    console.warn('[SyncService] triggerSync — offline, sync ignorée')
    return
  }

  _isSyncing = true

  // ✅ Cleanup orphelins au premier sync après boot
  if (!_hasRunCleanup) {
    try {
      const { cleanupOrphanCompilations } = await import('./compilationService')
      await cleanupOrphanCompilations()
      _hasRunCleanup = true
    } catch (err) {
      console.error('[syncService] Cleanup orphans failed at boot:', err)
      // Ne pas bloquer la sync
    }
  }

  _lastError = null
  _notify()

  try {
    const lastSyncAt = _getLastSyncAt(userId)
    const isFirstSync = !lastSyncAt

    console.warn(
      `[SyncService] triggerSync — userId=${userId}, isFirstSync=${isFirstSync}, lastSyncAt=${lastSyncAt}`
    )

    // Marge de 5s pour éviter de manquer des entités mises à jour pendant la fenêtre
    let sinceParam: string
    if (isFirstSync) {
      sinceParam = FIRST_SYNC_SENTINEL
    } else {
      const sinceWithMargin = new Date(
        new Date(lastSyncAt!).getTime() - CLOCK_MARGIN_MS
      ).toISOString()
      sinceParam = sinceWithMargin
    }

    // --- 5. Appel GET /sync/snapshot ---
    let snapshot: SnapshotResponse
    try {
      snapshot = await apiClient.get<SnapshotResponse>(
        `/sync/snapshot?since=${encodeURIComponent(sinceParam)}`
      )
    } catch (err) {
      _lastError = err instanceof Error ? err.message : 'Erreur réseau lors de la synchronisation'
      console.error('[SyncService] triggerSync — erreur GET /sync/snapshot:', _lastError)
      return
    }

    console.warn(
      `[SyncService] snapshot reçu — texts=${snapshot.texts.length}, versions=${snapshot.versions.length}, configs=${snapshot.configs.length}, serverTime=${snapshot.serverTime}`
    )

    // --- 6. Traiter les textes ---
    for (const text of snapshot.texts) {
      if (text.deletedAt) {
        const entityType = activeEntities.get(text.id)
        if (entityType === 'text') {
          window.dispatchEvent(
            new CustomEvent('repet:entity-deleted', {
              detail: { id: text.id, type: 'text' },
            })
          )
        }
        await deleteTextLocally(text.id)
      } else {
        await upsertTextFromServer({
          id: text.id,
          ownerId: text.ownerId,
          title: text.title,
          author: text.author,
          year: text.year,
          description: text.description,
          currentVersionId: text.currentVersionId,
          originalVersionId: text.originalVersionId,
          deletedAt: text.deletedAt ?? undefined,
          createdAt: text.createdAt,
          updatedAt: text.updatedAt,
        })
      }
    }

    // --- 7. Traiter les versions ---
    for (const version of snapshot.versions) {
      if (version.deletedAt) {
        const entityType = activeEntities.get(version.id)
        if (entityType === 'version') {
          window.dispatchEvent(
            new CustomEvent('repet:entity-deleted', {
              detail: { id: version.id, type: 'version' },
            })
          )
        }
        await deleteVersionLocally(version.id)
      } else {
        await upsertVersionFromServer({
          id: version.id,
          textId: version.textId,
          fileName: version.fileName,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ast: version.ast as any,
          contentHash: version.contentHash,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          voiceAssignments: version.voiceAssignments as any[],
          ownerVersionNumber: version.ownerVersionNumber,
          versionLabel: version.versionLabel,
          changeDescription: version.changeDescription,
          parentVersionId: version.parentVersionId,
          createdById: version.createdById,
          deviceId: version.deviceId,
          deletedAt: version.deletedAt ?? undefined,
          createdAt: version.createdAt,
          updatedAt: version.updatedAt,
        })
      }
    }

    // --- 7b. Traiter les textes partagés dans les groupes du membre ---
    // Ces textes appartiennent à d'autres utilisateurs : on les stocke en lecture
    // seule avec isShared=true. Ils ne seront pas purgés comme orphelins.
    const sharedTextIds = new Set<string>()
    const sharedVersionIds = new Set<string>()
    for (const entry of snapshot.sharedTexts ?? []) {
      const { text, version } = entry

      // Si l'utilisateur courant est le propriétaire du texte, il ne faut pas
      // le marquer isShared=true — il apparaît ici parce qu'il l'a partagé dans
      // un groupe, mais il en reste le propriétaire et doit pouvoir le modifier.
      // Son texte sera traité correctement via snapshot.texts (section 6).
      if (text.ownerId === userId) continue

      sharedTextIds.add(text.id)
      sharedVersionIds.add(version.id)

      // Upsert du texte avec isShared=true (le serverId = id car c'est un ID serveur)
      const existingText = await db.texts.get(text.id)
      if (existingText) {
        await db.texts.update(text.id, {
          title: text.title,
          author: text.author,
          year: text.year,
          description: text.description,
          currentVersionId: version.id,
          updatedAt: text.updatedAt,
          syncStatus: 'SYNCED' as const,
          isShared: true,
          sharedGroupId: entry.groupId,
        })
      } else {
        await db.texts.add({
          id: text.id,
          ownerId: text.ownerId,
          title: text.title,
          author: text.author,
          year: text.year,
          description: text.description,
          currentVersionId: version.id,
          originalVersionId: text.originalVersionId,
          createdAt: text.createdAt,
          updatedAt: text.updatedAt,
          lastOpenedAt: text.updatedAt,
          syncStatus: 'SYNCED',
          deletedAt: undefined,
          serverId: text.id,
          lastSyncedAt: text.updatedAt,
          serverUpdatedAt: text.updatedAt,
          isShared: true,
          sharedGroupId: entry.groupId,
        })
      }

      // Upsert de la version partagée
      await upsertVersionFromServer({
        id: version.id,
        textId: version.textId,
        fileName: version.fileName,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ast: version.ast as any,
        contentHash: version.contentHash,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        voiceAssignments: version.voiceAssignments as any[],
        ownerVersionNumber: version.ownerVersionNumber,
        versionLabel: version.versionLabel,
        changeDescription: version.changeDescription,
        parentVersionId: version.parentVersionId,
        createdById: version.createdById,
        deviceId: version.deviceId,
        deletedAt: undefined,
        createdAt: version.createdAt,
        updatedAt: version.updatedAt,
      })

      // Appliquer la config de l'invité pour cette version partagée (si fournie)
      if (entry.config) {
        await versionConfigService.applyServerConfig(entry.config)
      }
    }

    // Supprimer les textes partagés qui ne sont plus dans les groupes du membre
    // (à chaque sync, pas seulement isFirstSync — pour détecter les retraits de groupe en différentiel)
    {
      const localSharedTexts = await db.texts.filter((t) => !!t.isShared && !t.deletedAt).toArray()
      for (const t of localSharedTexts) {
        if (!sharedTextIds.has(t.id)) {
          // Si ce texte appartient à l'utilisateur courant, il a été incorrectement
          // marqué isShared=true (bug de cohérence) — corriger sans supprimer.
          if (t.ownerId === userId) {
            console.warn(
              `[SyncService] correction isShared — texte propriétaire marqué partagé par erreur : ${t.id}`
            )
            await db.texts.update(t.id, { isShared: false, sharedGroupId: undefined })
          } else {
            await deleteTextLocally(t.id)
          }
        }
      }
    }

    // --- 8. Supprimer les entités locales orphelines (premier sync uniquement) ---
    // Le snapshot différentiel (since != epoch) ne retourne que les entités
    // modifiées depuis lastSyncAt : un texte non-modifié absent du snapshot
    // est toujours valide, pas orphelin. La suppression d'orphelins ne peut
    // être fiable que lors du premier sync (snapshot exhaustif, since=epoch).
    if (isFirstSync) {
      // IDs non-supprimés retournés par le snapshot exhaustif
      const snapshotTextIds = new Set(snapshot.texts.filter((t) => !t.deletedAt).map((t) => t.id))
      const snapshotVersionIds = new Set(
        snapshot.versions.filter((v) => !v.deletedAt).map((v) => v.id)
      )

      // Textes locaux de cet utilisateur (y compris ceux incorrectement marqués isShared=true
      // qui ont été corrigés plus haut — on les inclut pour détecter les orphelins)
      const localTexts = await db.texts
        .filter((t) => t.ownerId === userId && !t.deletedAt)
        .toArray()

      for (const localText of localTexts) {
        if (!snapshotTextIds.has(localText.id)) {
          // Orphelin — absent du snapshot exhaustif mais présent localement
          const entityType = activeEntities.get(localText.id)
          if (entityType === 'text') {
            window.dispatchEvent(
              new CustomEvent('repet:entity-deleted', {
                detail: { id: localText.id, type: 'text' },
              })
            )
          }
          await deleteTextLocally(localText.id)
        }
      }

      // Versions locales dont le texte appartient à cet utilisateur
      // (excluant les versions de textes partagés — gérées par sharedTextIds)
      const localVersions = await db.textVersions.filter((v) => !v.deletedAt).toArray()

      const userTextIds = new Set(localTexts.map((t) => t.id))

      for (const localVersion of localVersions) {
        // Ne pas purger les versions de textes partagés
        if (sharedVersionIds.has(localVersion.id)) continue
        if (userTextIds.has(localVersion.textId) && !snapshotVersionIds.has(localVersion.id)) {
          const entityType = activeEntities.get(localVersion.id)
          if (entityType === 'version') {
            window.dispatchEvent(
              new CustomEvent('repet:entity-deleted', {
                detail: { id: localVersion.id, type: 'version' },
              })
            )
          }
          await deleteVersionLocally(localVersion.id)
        }
      }
    }

    // --- 8b. Traiter les groupes ---
    const snapshotGroupIds = new Set<string>()
    for (const group of snapshot.groups ?? []) {
      snapshotGroupIds.add(group.id)
      await upsertGroupFromServer({
        id: group.id,
        name: group.name,
        description: group.description,
        ownerId: group.ownerId,
        createdAt: group.createdAt,
        updatedAt: group.updatedAt,
      })
    }

    // --- 8c. Traiter les membres de groupes ---
    // Regrouper les membres par groupId pour la purge par groupe
    const snapshotMemberIdsByGroup = new Map<string, Set<string>>()
    for (const member of snapshot.groupMembers ?? []) {
      await upsertGroupMemberFromServer({
        id: member.id,
        groupId: member.groupId,
        userId: member.userId,
        role: member.role,
        joinedAt: member.joinedAt,
        userEmail: member.userEmail,
        userName: member.userName,
      })
      if (!snapshotMemberIdsByGroup.has(member.groupId)) {
        snapshotMemberIdsByGroup.set(member.groupId, new Set())
      }
      snapshotMemberIdsByGroup.get(member.groupId)!.add(member.id)
    }

    // --- 8d. Traiter les textes partagés dans les groupes ---
    const snapshotGroupTextIdsByGroup = new Map<string, Set<string>>()
    for (const gt of snapshot.groupTexts ?? []) {
      await upsertGroupTextFromServer({
        id: gt.id,
        groupId: gt.groupId,
        textId: gt.textId,
        versionId: gt.versionId,
        publishedById: gt.publishedById,
        publishedAt: gt.publishedAt,
        publishedByName: gt.publishedByName,
        textTitle: gt.textTitle,
        textAuthor: gt.textAuthor,
        versionLabel: gt.versionLabel,
        ownerDisplayName: gt.ownerDisplayName,
        hasAudio: gt.hasAudio,
        audioDuration: gt.audioDuration,
      })
      if (!snapshotGroupTextIdsByGroup.has(gt.groupId)) {
        snapshotGroupTextIdsByGroup.set(gt.groupId, new Set())
      }
      snapshotGroupTextIdsByGroup.get(gt.groupId)!.add(gt.id)
    }

    // --- 8e. Purge des entités de groupe obsolètes ---
    // Le snapshot groupes est TOUJOURS exhaustif (liste complète des groupes du membre),
    // contrairement aux textes/versions qui sont différentiels (since=lastSyncAt).
    // On purge donc à chaque sync, pas seulement au premier.
    await purgeStaleGroups(userId, snapshotGroupIds)

    // Purger les membres et textes obsolètes groupe par groupe
    // (seulement pour les groupes présents dans le snapshot — les groupes
    // supprimés sont déjà purgés par purgeStaleGroups ci-dessus)
    for (const [groupId, memberIds] of snapshotMemberIdsByGroup) {
      await purgeStaleGroupMembers(groupId, memberIds)
    }
    for (const [groupId, gtIds] of snapshotGroupTextIdsByGroup) {
      await purgeStaleGroupTexts(groupId, gtIds)
    }

    // --- 9. Traiter les configs (LWW) ---
    for (const config of snapshot.configs) {
      await versionConfigService.applyServerConfig(config)
    }

    // --- 10. Pousser les configs PENDING ---
    await versionConfigService.pushPendingConfigs(userId, true)

    // --- 11. Sync des audios manquants (non-bloquant) ---
    _syncMissingAudios().catch(() => {
      // Silencieux
    })

    // --- 12. Mettre à jour lastSyncAt ---
    _setLastSyncAt(userId, snapshot.serverTime)
    _lastError = null
    console.warn(`[SyncService] sync terminée — lastSyncAt mis à jour: ${snapshot.serverTime}`)
  } catch (err) {
    _lastError = err instanceof Error ? err.message : 'Erreur inconnue lors de la synchronisation'
    console.error('[SyncService] triggerSync — erreur inattendue:', _lastError)
  } finally {
    _isSyncing = false
    _notify()
  }
}

// ============================================================================
// initSyncService / stopSyncService
// ============================================================================

export function initSyncService(userId: string): void {
  // Arrêter un éventuel polling précédent
  stopSyncService()

  _currentUserId = userId

  console.warn(`[SyncService] initSyncService — userId=${userId}, déclenchement sync immédiate`)

  // Sync immédiate
  triggerSync(userId).catch(() => {
    // Erreur capturée dans _lastError
  })

  // Polling toutes les 60s
  _pollingTimer = setInterval(() => {
    if (_currentUserId) {
      console.warn(`[SyncService] polling — triggerSync userId=${_currentUserId}`)
      triggerSync(_currentUserId).catch(() => {
        // Erreur capturée dans _lastError
      })
    }
  }, SYNC_INTERVAL_MS)

  console.warn(`[SyncService] polling démarré (interval=${SYNC_INTERVAL_MS}ms)`)
}

export function stopSyncService(): void {
  if (_pollingTimer !== null) {
    clearInterval(_pollingTimer)
    _pollingTimer = null
  }
  _currentUserId = null
  _isSyncing = false
  _notify()
}

// ============================================================================
// getSyncState / onSyncStateChange
// ============================================================================

export function getSyncState(): SyncState {
  return _getSyncState()
}

/**
 * S'abonner aux changements d'état de synchronisation.
 * Retourne une fonction de désinscription.
 */
export function onSyncStateChange(callback: (state: SyncState) => void): () => void {
  _observers.add(callback)
  return () => {
    _observers.delete(callback)
  }
}

export const syncService = {
  initSyncService,
  stopSyncService,
  triggerSync,
  getSyncState,
  onSyncStateChange,
  registerActiveEntity,
  unregisterActiveEntity,
}
